import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, ArrowsClockwise, CheckCircle, Plug, Info, ArrowLeft, Browsers, ArrowSquareOut, GoogleChromeLogo, DeviceMobile } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { CHROME_STORE_URL } from "@/lib/extensionConstants";

// iter v29 r216 — User M1505: «l'estensione dall'app iOS funziona/serve?»
// Risposta onesta: NO. Chrome su iOS non supporta le estensioni (limite Apple).
// Detection ambiente native iOS → la card estensione si trasforma in nota
// "usa il tuo PC per installare l'estensione".
const isNativeIOS = () => {
  try {
    if (typeof window === "undefined") return false;
    return !!window.Capacitor?.isNativePlatform?.();
  } catch {
    return false;
  }
};


const PORTAL_GUIDES = [
  {
    name: "Idealista",
    slug: "idealista",
    // iter v29 r222 — Colori brand reali (utente ha condiviso i logo
    // ufficiali): Idealista usa il famoso verde-lime fluorescente,
    // Immobiliare.it il blu brand. Testo dark su lime, bianco su blu.
    logo_color: "#D7F600",
    text_color: "#1C1C1A",
    backoffice_url: "https://professionali.idealista.it/",
    backoffice_label: "Apri area Professionisti Idealista",
    steps: [
      "Accedi alla tua area professionisti Idealista",
      "Vai su Impostazioni → Integrazioni XML/MLS",
      "Incolla l'URL del feed qui sopra nel campo 'URL del file XML'",
      "Imposta frequenza aggiornamento: ogni 4 ore",
      "Salva. I tuoi immobili verranno pubblicati automaticamente al prossimo sync.",
    ],
  },
  {
    name: "Immobiliare.it",
    slug: "immobiliare",
    logo_color: "#1F5AAF",
    text_color: "#FFFFFF",
    backoffice_url: "https://areariservata.immobiliare.it/",
    backoffice_label: "Apri area Agenzie Immobiliare.it",
    steps: [
      "Area riservata agenzie → Impostazioni feed",
      "Seleziona 'Feed XML personalizzato'",
      "Incolla l'URL del feed qui sopra",
      "Tipologia feed: 'custom MLS Italia'",
      "Salva e attendi la prima sincronizzazione (2-6h).",
    ],
  },
];

// iter v29 r220 — Tracker locale "Ho incollato l'URL su questo portale".
// Persistito in localStorage (chiave per slug). Vita: persiste finché
// l'agente non rigenera il feed (allora va resettato).
const CONFIG_LS_KEY = "pronto:portali:configured";
const loadConfigured = () => {
  try {
    const raw = localStorage.getItem(CONFIG_LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};
const saveConfigured = (obj) => {
  try { localStorage.setItem(CONFIG_LS_KEY, JSON.stringify(obj)); } catch { /* quota */ }
};


export default function Portali() {
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  // iter v29 r216 — validator pre-flight (annuncio per annuncio)
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  // iter v29 r220 — tracker "configurato sui portali" (localStorage)
  const [configured, setConfigured] = useState(() => loadConfigured());

  const toggleConfigured = (slug) => {
    setConfigured((prev) => {
      const next = { ...prev };
      if (next[slug]) {
        delete next[slug];
      } else {
        next[slug] = new Date().toISOString();
      }
      saveConfigured(next);
      return next;
    });
  };

  useEffect(() => {
    api.get("/feed/info").then(({ data }) => setFeed(data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/feed/validate")
      .then(({ data }) => setValidation(data))
      .catch(() => setValidation({ total: 0, items: [] }));
  }, []);

  const runValidate = async () => {
    setValidating(true);
    try {
      const { data } = await api.get("/feed/validate");
      setValidation(data);
      toast.success("Controllo eseguito");
    } catch {
      toast.error("Controllo non riuscito");
    } finally {
      setValidating(false);
    }
  };

  const copyTarget = async (key, url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
      toast.success("Link copiato");
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Rigenerare i feed interromperà le sincronizzazioni in corso sui portali. Dovrai incollare di nuovo i nuovi link ovunque. Continuare?")) return;
    setRegenerating(true);
    try {
      const { data } = await api.post("/feed/regenerate");
      const fresh = await api.get("/feed/info").then((r) => r.data).catch(() => null);
      if (fresh) setFeed(fresh);
      else if (data?.slug) setFeed((f) => f ? { ...f, slug: data.slug } : f);
      // iter v29 r220 — Reset stato "configurato sui portali" perché i
      // vecchi URL sono morti, l'agente DEVE incollare di nuovo.
      setConfigured({});
      saveConfigured({});
      toast.success("Nuovi link generati");
    } catch {
      toast.error("Errore nella rigenerazione");
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <AppShell title="Portali" breadcrumb="Portali">
        <Link to="/dashboard" className="text-sm text-[#6B6A65] hover:text-[#1C1C1A] inline-flex items-center gap-1.5 mb-5">
          <ArrowLeft size={14} weight="bold" /> Dashboard
        </Link>

        <div className="mb-10">
          <span className="overline">Integrazioni</span>
          <h1 className="font-editorial text-4xl sm:text-5xl mt-3 leading-tight tracking-tight">
            Connetti i tuoi<br/>
            <span className="italic-accent text-[#7C8B6E]">portali.</span>
          </h1>
          <p className="mt-4 text-[15px] text-[#6B6A65] max-w-xl leading-relaxed">
            Con una configurazione una tantum i tuoi immobili pubblicati
            vengono sincronizzati automaticamente su Immobiliare.it e Idealista.
            Nessun copia-incolla, nessun doppio lavoro.
          </p>
        </div>

        {/* iter v30 r100 — User M1750: «metti l'estensione SOPRA il feed XML
            perché è quella che si capisce subito; il feed lo abbasserei e
            scriverei i testi più sintetici». Riordinata la pagina: Extension
            in cima, Feed XML sotto, entrambi con testi più stringati. */}
        {isNativeIOS() ? (
          <div className="card-flat p-5 mb-10 bg-[#FFFAEC] border border-[#E8D9A8]" data-testid="extension-ios-note">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-[#F2D89E] flex items-center justify-center flex-shrink-0">
                <DeviceMobile size={18} weight="bold" className="text-[#8B6914]" />
              </div>
              <div className="flex-1">
                <h3 className="font-editorial text-[18px] text-[#1C1C1A] leading-tight">
                  Sei sull&apos;app del telefono.
                </h3>
                <p className="text-[13px] text-[#1C1C1A] mt-1.5 leading-snug">
                  Per pubblicare dal telefono usa il <strong>feed XML qui sotto</strong>: copi l&apos;URL una volta, e i portali leggono tutto da soli.
                </p>
                <p className="text-[12px] text-[#6B6A65] mt-2 leading-snug">
                  L&apos;<strong>estensione Chrome</strong> si installa solo da computer (iPhone non lo permette). Quando vai sul tuo PC, la trovi qui.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="card-flat p-7 mb-10 bg-white border border-[#E5E7E0]" data-testid="extension-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-md bg-[#7C8B6E] text-white flex items-center justify-center flex-shrink-0">
                <Browsers size={22} weight="regular" />
              </div>
              <div className="flex-1">
                <span className="overline text-[#7C8B6E]">Estensione Chrome · gratis</span>
                <h3 className="font-editorial text-xl mt-1 mb-2">Pronto Publisher</h3>
                <p className="text-sm text-[#1C1C1A] leading-relaxed">
                  Mentre compili il modulo del portale, tiene i dati dell&apos;annuncio (foto, prezzo, descrizione)
                  sempre in vista a destra: copi e incolli senza cambiare scheda.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <a
                    href={CHROME_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary text-sm inline-flex items-center gap-1.5"
                    data-testid="extension-download-btn"
                  >
                    <GoogleChromeLogo size={14} weight="fill" />
                    Aggiungi a Chrome
                    <ArrowSquareOut size={13} weight="bold" />
                  </a>
                  <a
                    href="/dashboard/extension"
                    className="btn-outline text-sm inline-flex items-center gap-1.5"
                    data-testid="extension-install-guide-btn"
                  >
                    Come funziona
                  </a>
                </div>
                <details className="mt-4 text-[12px] text-[#6B6A65] group">
                  <summary className="cursor-pointer list-none inline-flex items-center gap-1 hover:text-[#1C1C1A]">
                    <span className="underline decoration-dotted underline-offset-4">Cosa fa esattamente</span>
                    <span className="text-[10px] group-open:rotate-180 transition-transform">▾</span>
                  </summary>
                  <div className="mt-2 pl-3 border-l-2 border-[#E5E7E0] space-y-1.5">
                    <p>· Funziona su Immobiliare.it e Idealista (vendita + affitto).</p>
                    <p>· Pannello sempre a destra con tutti i dati dell&apos;annuncio.</p>
                    <p>· Le credenziali del portale restano sul portale: non passano da Pronto.</p>
                  </div>
                </details>
              </div>
            </div>
          </div>
        )}

        {/* iter v29 r215 — Feed XML (sopra: extension, sotto: feed).
            User M1504: «XML solo Idealista + Immobiliare. Funzioni assolutamente.»
            Backend già completo (routes/feed.py), ora la UI espone gli URL reali. */}
        <div className="card-flat p-7 mb-10 bg-[#F1F4ED]" data-testid="feed-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className="overline text-[#7C8B6E]">Il tuo feed XML</span>
              <h3 className="font-editorial text-xl mt-1">Un URL per portale, da copiare una volta sola</h3>
            </div>
            {feed?.properties_in_feed > 0 ? (
              <span className="text-xs px-2.5 py-1 bg-[#7C8B6E] text-white rounded-full font-semibold inline-flex items-center gap-1" data-testid="feed-active-badge">
                <CheckCircle size={11} weight="fill" />
                Attivo · {feed.properties_in_feed} {feed.properties_in_feed === 1 ? "immobile" : "immobili"}
              </span>
            ) : (
              <span className="text-xs px-2.5 py-1 bg-[#F2D89E] text-[#8B6914] rounded-full font-semibold" data-testid="feed-empty-badge">
                Vuoto · pubblica un immobile
              </span>
            )}
          </div>

          {/* iter v30 r100 — Testo principale ridotto al minimo, dettagli
              spostati in un <details> collassato (utenti già esperti non
              hanno bisogno della spiegazione lunga). */}
          <p className="mt-3 text-[13.5px] text-[#1C1C1A] leading-relaxed">
            Copi l&apos;URL del feed una volta sola sull&apos;area professionisti del portale. Da lì i tuoi
            immobili viaggiano da soli ogni 4 ore.
          </p>

          {/* Plain-language explainer — click to open */}
          <details className="mt-3 text-sm group" data-testid="xml-explainer">
            <summary className="cursor-pointer list-none inline-flex items-center gap-1.5 text-[#7C8B6E] hover:text-[#1C1C1A]">
              <Info size={14} weight="bold" />
              <span className="underline decoration-dotted underline-offset-4">Cos&apos;è esattamente</span>
              <span className="text-[#6B6A65] text-xs group-open:rotate-180 transition-transform">▾</span>
            </summary>
            <div className="mt-3 p-4 bg-white border border-[#E5E7E0] rounded text-[13px] text-[#1C1C1A] leading-relaxed space-y-2">
              <p>
                Un <strong>feed XML</strong> è una lista pubblica dei tuoi annunci che Pronto tiene
                sempre aggiornata. Immobiliare.it e Idealista la rileggono da soli ogni 4 ore.
              </p>
              <p className="text-[#6B6A65]">
                Tu carichi foto e dati su Pronto, il feed si aggiorna da solo, i portali pescano
                le novità. Zero copia-incolla.
              </p>
            </div>
          </details>

          {!loading && feed?.properties_in_feed === 0 && (
            <div className="mt-5 p-4 bg-white border border-[#E5E7E0] rounded-lg flex items-start gap-3" data-testid="feed-empty-hint">
              <div className="w-9 h-9 rounded-full bg-[#F2D89E] flex items-center justify-center flex-shrink-0">
                <Info size={16} weight="bold" className="text-[#8B6914]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] text-[#1C1C1A] font-semibold mb-1">Pubblica almeno un immobile per iniziare</p>
                <p className="text-[12px] text-[#6B6A65] leading-snug">
                  Il feed XML mostra solo immobili con vetrina pubblica attiva. Vai sui tuoi annunci, apri il dettaglio e abilita "Pubblica vetrina".
                </p>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={regenerate}
            disabled={regenerating || loading}
            className="mt-5 inline-flex items-center gap-1.5 text-[12px] text-[#6B6A65] hover:text-[#1C1C1A] disabled:opacity-50"
            data-testid="feed-regenerate-btn"
          >
            <ArrowsClockwise size={12} weight="bold" className={regenerating ? "animate-spin" : ""} />
            {regenerating ? "Sto rigenerando…" : "Rigenera URL (in caso di compromissione)"}
          </button>
        </div>

        {/* iter v29 r216 — Pre-flight validator: dice all'agente PRIMA
            di passare l'URL al portale quali annunci passeranno. */}
        {validation && validation.total > 0 && (
          <div className="card-flat p-5 sm:p-6 mb-10 bg-white border border-[#E5E7E0]" data-testid="validator-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <span className="overline text-[#7C8B6E]">Controllo pubblicabilità</span>
                <h3 className="font-editorial text-xl mt-1">I tuoi annunci sono pronti per i portali?</h3>
                <p className="text-[12.5px] text-[#6B6A65] mt-1 leading-snug">
                  Controllo automatico secondo le regole pubbliche di Idealista e Immobiliare.it.
                </p>
              </div>
              <button
                onClick={runValidate}
                disabled={validating}
                className="text-[12px] text-[#7C8B6E] hover:text-[#1C1C1A] inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="validator-recheck"
              >
                <ArrowsClockwise size={12} weight="bold" className={validating ? "animate-spin" : ""} />
                {validating ? "Sto controllando…" : "Ricontrolla"}
              </button>
            </div>

            {/* Summary stats */}
            <div className="mt-4 grid grid-cols-3 gap-2">
              <div className="text-center bg-[#E8F0DD] border border-[#7C8B6E]/30 rounded-lg p-3" data-testid="validator-stat-ok">
                <div className="font-editorial text-[24px] text-[#3D5A2C] leading-none">{validation.ok}</div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#5C6B4E] mt-1">Pronti</div>
              </div>
              <div className="text-center bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="validator-stat-warn">
                <div className="font-editorial text-[24px] text-amber-700 leading-none">{validation.warning}</div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mt-1">Da rifinire</div>
              </div>
              <div className="text-center bg-red-50 border border-red-200 rounded-lg p-3" data-testid="validator-stat-blocked">
                <div className="font-editorial text-[24px] text-red-700 leading-none">{validation.blocked}</div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-red-700 mt-1">Bloccati</div>
              </div>
            </div>

            {/* Lista annuncio per annuncio */}
            <div className="mt-4 space-y-2" data-testid="validator-list">
              {validation.items.map((it) => {
                const isOk = it.status === "ok";
                const isWarn = it.status === "warning";
                const cls = isOk
                  ? "bg-white border-[#7C8B6E]/30"
                  : isWarn
                    ? "bg-amber-50/50 border-amber-200"
                    : "bg-red-50/50 border-red-200";
                const StatusIcon = isOk ? CheckCircle : Info;
                const statusColor = isOk ? "text-[#5C6B4E]" : isWarn ? "text-amber-700" : "text-red-700";
                return (
                  <div
                    key={it.property_id}
                    className={`border rounded-lg p-3 ${cls}`}
                    data-testid={`validator-item-${it.property_id}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <StatusIcon size={16} weight="fill" className={`${statusColor} flex-shrink-0 mt-0.5`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="text-[13.5px] font-semibold text-[#1C1C1A] truncate">
                            {it.title}
                          </div>
                          <div className="text-[11px] text-[#6B6A65]">
                            {it.city || "—"} · {it.price ? `€${Number(it.price).toLocaleString("it-IT")}` : "no prezzo"} · {it.photos_count} {it.photos_count === 1 ? "foto" : "foto"}
                          </div>
                        </div>
                        {it.issues.length > 0 && (
                          <ul className="mt-1.5 space-y-1">
                            {it.issues.map((iss, idx) => (
                              <li key={idx} className="text-[12px] text-red-700 leading-snug flex items-start gap-1">
                                <span className="font-bold">·</span>
                                <span>{iss.label}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {it.warnings.length > 0 && (
                          <ul className="mt-1.5 space-y-1">
                            {it.warnings.map((w, idx) => (
                              <li key={idx} className="text-[12px] text-amber-700 leading-snug flex items-start gap-1">
                                <span>·</span>
                                <span>{w.label}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {(it.issues.length > 0 || it.warnings.length > 0) && (
                          <Link
                            to={`/dashboard/property/${it.property_id}`}
                            className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#7C8B6E] hover:text-[#1C1C1A]"
                            data-testid={`validator-fix-${it.property_id}`}
                          >
                            Apri e sistema →
                          </Link>
                        )}
                        {it.status === "ok" && it.public_id && (
                          <Link
                            to={`/dashboard/listings/${it.public_id}/pubblica-veloce`}
                            className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#5C6B4E] hover:text-[#1C1C1A]"
                            data-testid={`validator-publish-${it.property_id}`}
                          >
                            Pubblica veloce →
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mt-4 text-[11px] text-[#9A968B] leading-snug">
              Idealista e Immobiliare.it controllano questi campi prima di pubblicare. Gli annunci "Bloccati" verranno rifiutati al primo aggiornamento. Quelli "Da rifinire" passano ma compaiono più in basso nei risultati.
            </p>
          </div>
        )}

        {/* iter v30 r100 — La card Estensione è stata spostata IN ALTO
            (dopo la hero, prima del Feed XML) per dare priorità visiva
            allo strumento più immediato. Il blocco JSX storico qui sotto è
            stato rimosso intenzionalmente. */}

        {/* Portal guides — each one shows its own URL inline */}
        <div className="mb-6 flex items-baseline justify-between gap-3 flex-wrap">
          <h2 className="font-editorial text-2xl tracking-tight" data-testid="guides-title">Configura ogni portale</h2>
          {/* iter v29 r220 — Pill riassuntiva: "1/2 portali configurati". */}
          {(() => {
            const total = PORTAL_GUIDES.length;
            const done = PORTAL_GUIDES.filter((pg) => !!configured[pg.slug]).length;
            const allDone = done === total;
            return (
              <span
                className={`text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 ${
                  allDone
                    ? "bg-[#E8F0DD] text-[#3D5A2C] border border-[#7C8B6E]/40"
                    : done > 0
                      ? "bg-amber-50 text-amber-700 border border-amber-200"
                      : "bg-[#F5F4F0] text-[#6B6A65] border border-[#E5E7E0]"
                }`}
                data-testid="portal-progress-pill"
              >
                <CheckCircle size={11} weight={allDone ? "fill" : "regular"} />
                {done}/{total} portali configurati
              </span>
            );
          })()}
        </div>
        <div className="space-y-5">
          {PORTAL_GUIDES.map((pg) => {
            const portalUrl = feed?.urls?.[pg.slug];
            const isCopied = copiedKey === pg.slug;
            const configDate = configured[pg.slug];
            const isConfigured = !!configDate;
            return (
            <details key={pg.slug} className="card-flat bg-white p-6 group" data-testid={`portal-guide-${pg.slug}`} open={!isConfigured}>
              <summary className="cursor-pointer flex items-center gap-4 list-none [&::-webkit-details-marker]:hidden">
                <div
                  className="w-10 h-10 rounded flex items-center justify-center font-bold text-sm"
                  style={{ backgroundColor: pg.logo_color, color: pg.text_color || "#FFFFFF" }}
                  aria-hidden
                >
                  {pg.name[0]}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-editorial text-lg tracking-tight">{pg.name}</h3>
                    {/* iter v29 r220 — Stato "configurato" inline al titolo. */}
                    {isConfigured && (
                      <span
                        className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[#E8F0DD] text-[#3D5A2C] border border-[#7C8B6E]/30"
                        title={`Configurato il ${new Date(configDate).toLocaleDateString("it-IT")}`}
                        data-testid={`portal-configured-${pg.slug}`}
                      >
                        <CheckCircle size={10} weight="fill" /> Configurato
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#6B6A65] mt-0.5">
                    {isConfigured
                      ? `Configurato il ${new Date(configDate).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })} · clicca per rivedere`
                      : `${pg.steps.length} passi · ${feed?.properties_in_feed > 0 ? `${feed.properties_in_feed} ${feed.properties_in_feed === 1 ? "immobile attivo" : "immobili attivi"}` : "feed pronto · vuoto"}`}
                  </p>
                </div>
                <Plug size={16} className="text-[#7C8B6E] group-open:rotate-90 transition-transform" />
              </summary>

              {/* iter v29 r215 — URL XML reale + bottone copia.
                  Feed funzionante: backend routes/feed.py genera lo schema
                  Idealista/Immobiliare con tutti i campi richiesti. */}
              <div className="mt-5 p-4 bg-white border border-[#E5E7E0] rounded" data-testid={`portal-url-${pg.slug}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-full bg-[#E8F0DD] flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={13} weight="fill" className="text-[#5C6B4E]" />
                  </div>
                  <div className="text-[12.5px] font-semibold text-[#1C1C1A]">
                    URL XML per {pg.name}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <code
                    className="flex-1 min-w-0 text-[11.5px] font-mono bg-[#F8F7F2] px-2.5 py-2 rounded border border-[#EAE7DF] text-[#1C1C1A] truncate"
                    data-testid={`portal-url-text-${pg.slug}`}
                    title={portalUrl}
                  >
                    {portalUrl || "Caricamento…"}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyTarget(pg.slug, portalUrl)}
                    disabled={!portalUrl}
                    className={`shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded text-[12px] font-semibold transition-colors ${
                      isCopied
                        ? "bg-[#5C6B4E] text-white"
                        : "bg-[#1C1C1A] hover:bg-black text-white disabled:opacity-50"
                    }`}
                    data-testid={`portal-copy-${pg.slug}`}
                  >
                    {isCopied ? (
                      <>
                        <CheckCircle size={12} weight="fill" />
                        Copiato
                      </>
                    ) : (
                      <>
                        <Copy size={12} weight="bold" />
                        Copia
                      </>
                    )}
                  </button>
                </div>
                <a
                  href={portalUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#7C8B6E] hover:text-[#1C1C1A] font-medium"
                  data-testid={`portal-preview-${pg.slug}`}
                >
                  <ArrowSquareOut size={11} weight="bold" />
                  Apri l'XML in una scheda per controllare
                </a>
              </div>

              {/* iter v29 r220 — Deep-link al backoffice + toggle "configurato".
                  iter v29 r221 — Pulsante in PIENO color brand portale
                  (bg=logo_color, testo bianco), per coerenza visiva immediata
                  con Idealista (rosso-coral) e Immobiliare.it (arancio). */}
              <div className="mt-4 flex flex-wrap items-center gap-2" data-testid={`portal-actions-${pg.slug}`}>
                <a
                  href={pg.backoffice_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded text-[12.5px] font-semibold shadow-sm hover:opacity-90 active:opacity-80 transition-opacity"
                  style={{ backgroundColor: pg.logo_color, color: pg.text_color || "#FFFFFF" }}
                  data-testid={`portal-backoffice-${pg.slug}`}
                  title={`Apri il backoffice di ${pg.name} per incollare l'URL`}
                >
                  <ArrowSquareOut size={12} weight="bold" />
                  {pg.backoffice_label}
                </a>
                <button
                  type="button"
                  onClick={() => toggleConfigured(pg.slug)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded text-[12.5px] font-semibold transition-colors ${
                    isConfigured
                      ? "bg-[#E8F0DD] text-[#3D5A2C] border border-[#7C8B6E]/40 hover:bg-[#DCE8CB]"
                      : "bg-[#7C8B6E] text-white border border-[#7C8B6E] hover:bg-[#6B7A5D]"
                  }`}
                  data-testid={`portal-toggle-${pg.slug}`}
                >
                  <CheckCircle size={12} weight={isConfigured ? "fill" : "regular"} />
                  {isConfigured ? "Disfà · non l'ho configurato" : "Ho incollato l'URL su " + pg.name}
                </button>
              </div>

              <ol className="mt-5 space-y-3 pl-2">
                {pg.steps.map((s, i) => (
                  <li key={i} className="flex gap-3 text-sm text-[#1C1C1A]">
                    <span className="w-6 h-6 rounded-full bg-[#F1F4ED] text-[#7C8B6E] flex items-center justify-center text-xs font-semibold flex-shrink-0">{i + 1}</span>
                    <span className="pt-0.5 leading-relaxed">{s}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-5 pt-4 border-t border-[#E5E7E0] flex items-center gap-3 text-xs">
                <CheckCircle size={14} weight="fill" className="text-[#5C6B4E]" />
                <span className="text-[#6B6A65]">
                  Una volta incollato l'URL su {pg.name}, ogni nuovo immobile che pubblichi su Pronto apparirà automaticamente al loro prossimo aggiornamento (in genere ogni 4-6 ore).
                </span>
              </div>
            </details>
            );
          })}
        </div>

        <div className="mt-10 p-5 bg-[#F1F4ED] rounded" data-testid="help-note">
          <p className="text-sm text-[#1C1C1A]">
            <strong>Problemi con la configurazione?</strong>
          </p>
          <p className="mt-1 text-sm text-[#6B6A65]">
            Apri il form <Link to="/contatti?argomento=bug" className="text-[#7C8B6E] underline">Contattaci</Link> indicando il portale: ti rispondiamo entro 24h e, se sei Pro, ti aiutiamo a completare il setup.
          </p>
        </div>
      </AppShell>
  );
}
